import React, { memo, ReactElement, useCallback, useState, useRef, useMemo, RefObject } from "react";
import classNames from "classnames";

import {
    getBillItemFontStyle,
    getBillItemMetaWindowSizeV4,
    getFlattenedSubDetails,
    getInfoTextFontStyles,
} from "@core-ui/helpers/BillDetailsHelper";
import { ItemValue } from "../item-value";
import { Tooltip } from "../tooltip";
import { FreeDelCommsStyling, PlanDetails, RenderingDetails } from "@swiggy-private/cart-types";
import { TooltipItem } from "@core-ui/components/tooltip-item";
import { RenderingDetailsSubDetails } from "@swiggy-private/cart-types";
import { RENDERING_DETAILS_TYPES, VALID_RENDERING_DETAILS_TYPE } from "@core-ui/constants/RenderingDetails";
import { AriaHidden, ScreenReaderOnly } from "@core-ui/components/accessibility";
import { replaceText } from "@core-ui/helpers/HtmlFormatter";
import { ElementObserver } from "@core-ui/components/ElementObserver";
import { ACCESSIBILITY_TEXTS, INFO_TEXT_LOGO_ID, LINE_ITEM_LOGO_ID } from "@core-ui/constants/BillDetails";
import { checkIfNumber } from "@core-ui/helpers/Utils";
import { isEmpty } from "@swiggy-private/js-utils";
import { BILL_DETAIL_ITEM_IDS } from "@swiggy-private/cart-test-ids";
import { OneLogoFull, OneLiteLogoFull } from "@core-ui/widgets/swiggy-one-logo";

import styles from "../BillDetailItem.scss";

export interface TooltipDisplayProps {
    itemData: RenderingDetails;
    onClickInfoIcon?: (key: string) => void;
    impressionTooltipInfoText?: (key: string) => void;
    onItemClick?: (itemType: string, itemData?: RenderingDetails) => void;
    planAddedRef?: RefObject<HTMLDivElement>;
    onImpressionDeliveryFeeLineItem: (label: string) => void;
    onImpressionTaxesAndChargesLineItem: (context: string) => void;
    onImpressionDeliveryFeeUnCollapsedLineItem: (label: string) => void;
    onImpressionOfferCashRewardLineItem?: (impressionData: string) => void;
    freeDelCommsStyles?: FreeDelCommsStyling;
    planDetails?: PlanDetails;
    canShowTooltip?: (itemData: RenderingDetails) => boolean;
    isSuperItem?: (key: string) => boolean;
    isOneLiteItem?: (key: string) => boolean;
    getTagFromDeliveryFeeKey?: (key: string) => string | undefined;
    getValidDeliveryFeeKeys?: () => string[];
    isItemValueRupee?: (itemData: RenderingDetails) => boolean;
}

const validTooltipItemTypes = [
    VALID_RENDERING_DETAILS_TYPE.SEPARATOR,
    VALID_RENDERING_DETAILS_TYPE.DISPLAY,
    VALID_RENDERING_DETAILS_TYPE.BUTTON,
    VALID_RENDERING_DETAILS_TYPE.TOOLTIPDISPLAY,
    VALID_RENDERING_DETAILS_TYPE.INFODISPLAY,
    VALID_RENDERING_DETAILS_TYPE.TITLE,
];

const TooltipDisplay = ({
    itemData,
    onClickInfoIcon,
    impressionTooltipInfoText,
    onItemClick,
    planAddedRef,
    onImpressionDeliveryFeeLineItem,
    onImpressionTaxesAndChargesLineItem,
    onImpressionDeliveryFeeUnCollapsedLineItem,
    onImpressionOfferCashRewardLineItem,
    freeDelCommsStyles,
    planDetails,
    canShowTooltip,
    isSuperItem,
    isOneLiteItem,
    getTagFromDeliveryFeeKey,
    getValidDeliveryFeeKeys,
    isItemValueRupee,
}: TooltipDisplayProps): ReactElement => {
    const [showInfo, setShowInfo] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [hasSentImpressionEvent, setHasSentImpressionEvent] = useState(false);
    const fontStyle = getBillItemFontStyle(itemData);
    const triggerRef = useRef<HTMLDivElement>(null);

    const { value, intermediateText, meta, icon } = itemData;

    const infoTextStyles = getInfoTextFontStyles(itemData, freeDelCommsStyles);

    const key = itemData.key.toUpperCase();
    const metaValueCTAVisible = !isEmpty(itemData?.meta?.valueCtaText);

    const rowClassName = classNames({ [styles.rowName]: true, [styles.rowNameCenterAlign]: isOneLiteItem?.(key) });

    const handleShowInfo = useCallback(() => {
        if (!showInfo && triggerRef?.current) {
            setShowInfo(true);
            typeof onClickInfoIcon === "function" && onClickInfoIcon(itemData.key);

            const { x, y, left, top, width } = triggerRef.current.getBoundingClientRect();
            // not all browsers gives x, y so we use left and top values in such cases
            setPosition({ x: (x || left) + width / 2, y: y || top });

            if (getValidDeliveryFeeKeys?.().includes(key)) {
                const obj = (itemData?.sub_details as RenderingDetailsSubDetails[])
                    .filter((item) => item.type?.toUpperCase() === VALID_RENDERING_DETAILS_TYPE.INFODISPLAY)
                    .map((item) => {
                        return {
                            key: item.key,
                            message: item.info_text,
                            originalPrice: item.intermediateText,
                            discountedPrice: item.value,
                        };
                    });
                onImpressionDeliveryFeeUnCollapsedLineItem(JSON.stringify(obj));
            }
        }
    }, [
        showInfo,
        onClickInfoIcon,
        itemData.key,
        itemData?.sub_details,
        onImpressionDeliveryFeeUnCollapsedLineItem,
        key,
    ]);

    const handleHideInfo = useCallback(() => {
        setShowInfo(false);
    }, []);

    const renderLineItems = useCallback((): ReactElement | ReactElement[] | undefined => {
        const data = itemData.sub_details;

        return (
            data &&
            data
                .filter(
                    (item: RenderingDetailsSubDetails) => validTooltipItemTypes.indexOf(item.type.toUpperCase()) !== -1,
                )
                .map((item: RenderingDetailsSubDetails, index: number) => (
                    <TooltipItem
                        key={`${item.key}_${index}`}
                        itemData={item}
                        isSuperItem={isSuperItem}
                        isItemValueRupee={isItemValueRupee}
                        isOneLiteItem={isOneLiteItem}
                    />
                ))
        );
    }, [itemData]);

    const handleItemClick = useCallback(() => {
        typeof onItemClick === "function" && onItemClick(itemData.key.toUpperCase(), itemData);
    }, [itemData, onItemClick]);

    const getScreenReaderText = useMemo((): string => {
        return `${itemData.display_text}  ${ACCESSIBILITY_TEXTS.RUPEE} ${value} ${itemData.info_text}  `
            .replace(" kms", ACCESSIBILITY_TEXTS.KILOMETRE)
            .replace("₹", ACCESSIBILITY_TEXTS.RUPEE);
    }, [itemData, value]);

    const isIntermediateTextNumber = useMemo((): boolean => checkIfNumber(intermediateText), [intermediateText]);

    const displayText = useMemo(() => {
        return itemData?.meta?.logoId
            ? replaceText(
                  itemData.display_text?.replace(LINE_ITEM_LOGO_ID, itemData?.meta?.logoId),
                  styles.displayTextLogo,
              )
            : itemData.display_text;
    }, [itemData?.meta?.logoId, itemData.display_text]);

    const onViewEnter = useCallback(() => {
        if (!hasSentImpressionEvent) {
            impressionTooltipInfoText?.(itemData.key);

            if (getValidDeliveryFeeKeys?.().includes(key)) {
                const tag = getTagFromDeliveryFeeKey?.(itemData.key);
                const impressionData: Record<string, unknown> = {
                    message: itemData.info_text,
                    originalPrice: itemData.intermediateText,
                    discountedPrice: itemData.value,
                    ctaText: itemData.meta?.valueCtaText,
                    ...planDetails,
                };
                if (!isEmpty(tag)) {
                    impressionData.tag = tag;
                }
                onImpressionDeliveryFeeLineItem(JSON.stringify(impressionData));
            }

            if (key === RENDERING_DETAILS_TYPES.TAXES_AND_CHARGES) {
                const flattenedObject = getFlattenedSubDetails(itemData.sub_details || []);
                onImpressionTaxesAndChargesLineItem(JSON.stringify(flattenedObject));
            }

            if (key === RENDERING_DETAILS_TYPES.OFFER_CASH_REWARD) {
                const impressionData: Record<string, unknown> = {
                    message: itemData.display_text,
                    value: itemData.value,
                };
                onImpressionOfferCashRewardLineItem?.(JSON.stringify(impressionData));
            }
        }

        setHasSentImpressionEvent(true);
    }, [
        hasSentImpressionEvent,
        impressionTooltipInfoText,
        itemData.key,
        itemData.info_text,
        itemData.intermediateText,
        itemData.value,
        itemData.meta?.valueCtaText,
        itemData.sub_details,
        key,
        onImpressionDeliveryFeeLineItem,
        planDetails,
        onImpressionTaxesAndChargesLineItem,
        onImpressionOfferCashRewardLineItem,
        itemData.display_text,
    ]);

    const infoTextClassNames = classNames(styles.displayInfoText, {
        [styles.displayInfoTextTooltipDisplay]: true,
        [styles.displayInfoTextBold]: freeDelCommsStyles?.infoTextBold,
    });

    const rowClassForTooltip = classNames({
        [styles.tooltipTrigger]: true,
        [styles.tooltipTriggerDisableBorder]: metaValueCTAVisible,
    });

    const tooltipDisplayTxtClass = classNames({
        [styles.addBorderOnTooltipHeading]: metaValueCTAVisible,
        [styles.tooltipDisplayTxtFlex]: true,
    });

    return (
        <ElementObserver onViewEnter={onViewEnter}>
            <div className={styles.toolTipDisplay} style={fontStyle}>
                <div style={{ position: "relative" }}>
                    <ScreenReaderOnly as="div" text={getScreenReaderText} />
                </div>
                <AriaHidden as="div" className={styles.row}>
                    <div className={rowClassForTooltip}>
                        {canShowTooltip?.(itemData) && (
                            <div className={rowClassName} onClick={handleShowInfo} ref={triggerRef}>
                                {isSuperItem?.(key) ? <OneLogoFull className={styles.icon} /> : null}
                                {isOneLiteItem?.(key) ? <OneLiteLogoFull className={styles.icon} /> : null}

                                <div
                                    className={tooltipDisplayTxtClass}
                                    data-testid={BILL_DETAIL_ITEM_IDS.ITEM_NAME(itemData.key)}
                                >
                                    {itemData?.meta?.logoId
                                        ? replaceText(
                                              itemData.display_text?.replace(LINE_ITEM_LOGO_ID, itemData?.meta?.logoId),
                                              styles.displayTextLogo,
                                          )
                                        : itemData.display_text}
                                </div>
                            </div>
                        )}
                        {!canShowTooltip?.(itemData) && (
                            <div>
                                <div
                                    className={tooltipDisplayTxtClass}
                                    data-testid={BILL_DETAIL_ITEM_IDS.ITEM_NAME(itemData.key)}
                                >
                                    {displayText}
                                </div>
                            </div>
                        )}
                    </div>

                    {showInfo && (
                        <Tooltip
                            backgroundGradient={meta?.tooltipBgGradiantColor}
                            position={position}
                            hide={handleHideInfo}
                            widgetSize={getBillItemMetaWindowSizeV4(itemData)}
                            triggerRef={triggerRef}
                        >
                            {renderLineItems()}
                        </Tooltip>
                    )}
                    <div data-testid="billdetail-itemValue" ref={planAddedRef} className={styles.rowValue}>
                        <ItemValue
                            value={value}
                            intermediateText={intermediateText}
                            intermediateTextStyle={isIntermediateTextNumber ? styles.rupee : ""}
                            isNegative={itemData.is_negative == 1}
                            meta={meta}
                            icon={icon}
                            itemKey={itemData.key}
                        />
                        {!isEmpty(itemData?.meta?.valueCtaText) && (
                            <div
                                className={classNames(styles.displayInfoTextValue, styles.rowValueBullEyeXp)}
                                onClick={handleItemClick}
                                data-testid={`${itemData.key}_VALUE_CTA_TEXT`}
                            >
                                {itemData?.meta?.valueCtaText}
                            </div>
                        )}
                    </div>
                </AriaHidden>

                <AriaHidden as="div">
                    <div className={infoTextClassNames} style={infoTextStyles}>
                        {freeDelCommsStyles?.icon
                            ? replaceText(
                                  itemData.info_text?.replace(INFO_TEXT_LOGO_ID, freeDelCommsStyles?.icon || ""),
                                  styles.displayTextLogo,
                              )
                            : itemData.info_text}
                    </div>
                </AriaHidden>
            </div>
        </ElementObserver>
    );
};

export default memo(TooltipDisplay);
